import React from "react";
import { Link } from "react-router-dom";
import PageTopSlider from "../components/PageTopSlider";
import SEO from "../components/SEO";

export default function BlogPostLCA() {
  return (
    <div className="bg-[#FAF9F6] text-stone-800">
      <SEO
        title="LCA-krav i BR18 2025: Hvad betyder det for dit byggeri?"
        description="Fra 1. juli 2025 blev klimakravene til nybyggeri skærpet. Se grænseværdierne, hvem der er omfattet, og hvad byggepladsen betyder for din LCA."
        canonical="https://www.etdanmark.com/blog/lca-krav-br18-2025"
        ogImage="https://assets.auxodev.dk/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07546.jpg"
        ogType="article"
      />

      {/* TOP HEADER */}
      <PageTopSlider
        title="LCA-krav i BR18 2025: Hvad betyder det for dit byggeri?"
        subtitle="Fra 1. juli 2025 blev klimakravene til nybyggeri skærpet markant. Her er et overblik over de nye grænseværdier, byggeprocessen og hvad det kræver i praksis."
        badge="Faglig guide & viden"
        breadcrumbs={[
          { label: "Forside", to: "/" },
          { label: "LCA-beregning", to: "/lca-beregning" },
          { label: "LCA-krav i BR18 2025" },
        ]}
        actions={
          <Link
            to="/lca-beregning"
            className="px-6 py-3 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm shadow-md transition-all flex items-center gap-2"
          >
            <i className="fa-solid fa-calculator text-xs" aria-hidden="true"></i>
            <span>Få et uforpligtende tilbud på din LCA</span>
          </Link>
        }
        image={{
          url: "https://assets.auxodev.dk/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07546.jpg",
          alt: "LCA klimaberegning bygningsreglementet BR18",
        }}
      />

      {/* ARTIKEL INDHOLD */}
      <article className="py-16 sm:py-20 bg-white border-b border-stone-200/70">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          {/* Metadata forfatter */}
          <div className="flex flex-wrap items-center gap-4 pb-8 mb-10 border-b border-stone-200 text-xs text-stone-500">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#165B4C] text-white flex items-center justify-center font-bold text-xs">
                LL
              </div>
              <span className="font-semibold text-stone-800">
                Lars Lauritzen, ET Danmark
              </span>
            </div>
            <span>•</span>
            <span>Certificeret Energikonsulent</span>
            <span>•</span>
            <span>Gældende fra 1. juli 2025</span>
          </div>

          <div className="space-y-8 text-stone-700 text-base sm:text-lg leading-relaxed">
            <h1 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight leading-tight">
              LCA-krav i BR18 2025: Hvad betyder det for dit byggeri?
            </h1>

            <p className="text-lg sm:text-xl text-stone-800 font-medium leading-relaxed">
              Bygger du nyt, er klimaberegningen ikke længere noget, kun store byggerier skal tænke på. Med de skærpede regler i bygningsreglementet fra 1. juli 2025 skal stort set alt nybyggeri – også det almindelige parcelhus – dokumentere sin CO2-udledning og holde sig under en fast grænse. Her er et overblik over, hvad reglerne betyder i praksis.
            </p>

            {/* AFSNIT 1 */}
            <div className="space-y-4 pt-4">
              <h2 className="text-2xl font-bold text-stone-900 tracking-tight">
                Fra 2023 til 2025: Hvad er ændret?
              </h2>
              <p>
                Kravet om en LCA-beregning (livscyklusvurdering) kom ind i bygningsreglementet den 1. januar 2023. I første omgang skulle nybyggeri, der var omfattet af energirammen, have en klimaberegning, men kun bygninger over 1.000 m² skulle overholde en grænseværdi. Den lå på 12,0 kg CO2-ækvivalenter pr. m² pr. år.
              </p>
              <p>
                For byggeri, hvor der er søgt byggetilladelse den 1. juli 2025 eller senere, er reglerne strammet markant:
              </p>
              <ul className="space-y-2 pl-4 list-disc marker:text-emerald-700 text-stone-700 text-sm sm:text-base">
                <li>Grænseværdien gælder nu for langt de fleste bygningstyper – uanset størrelse.</li>
                <li>Grænseværdien er differentieret efter bygningstype og ligger langt under de tidligere 12,0 kg.</li>
                <li>Selve byggeprocessen har fået sin egen grænseværdi.</li>
                <li>Flere bygningstyper er omfattet, fx sommerhuse, lagerhaller og parkeringshuse, og tilbygninger kan også være omfattet.</li>
              </ul>
              <p className="text-sm font-semibold text-stone-800 bg-stone-50 p-4 rounded-xl border border-stone-200">
                Det er datoen for ansøgningen om byggetilladelse, der afgør, hvilke regler dit projekt følger.
              </p>
            </div>

            {/* AFSNIT 2 */}
            <div className="space-y-4 pt-4">
              <h2 className="text-2xl font-bold text-stone-900 tracking-tight">
                De nye grænseværdier
              </h2>
              <p>
                Grænseværdierne angives i kg CO2e pr. m² pr. år og gælder for materialer, udskiftninger, energiforbrug i driften og affaldsbehandling, beregnet over 50 år:
              </p>

              <div className="bg-[#FAF9F6] border border-stone-200 rounded-2xl overflow-hidden my-6">
                <table className="w-full text-left text-xs sm:text-sm">
                  <thead className="bg-stone-100 text-stone-700 uppercase text-[11px] tracking-wider border-b border-stone-200">
                    <tr>
                      <th className="py-3 px-5 font-semibold">Bygningstype</th>
                      <th className="py-3 px-5 font-semibold text-right">Grænseværdi (kg CO2e/m²/år)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-200 text-stone-800">
                    <tr>
                      <td className="py-3 px-5 font-medium">Fritliggende enfamiliehuse, stuehuse samt række-, kæde- og dobbelthuse</td>
                      <td className="py-3 px-5 text-right font-bold text-emerald-800">6,7</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-5 font-medium">Sommerhuse og ferieboliger under 150 m²</td>
                      <td className="py-3 px-5 text-right font-bold text-emerald-800">4,0</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-5 font-medium">Sommerhuse og ferieboliger på mindst 150 m²</td>
                      <td className="py-3 px-5 text-right font-bold text-emerald-800">6,7</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-5 font-medium">Etageboliger samt kontor, handel, lager o.lign.</td>
                      <td className="py-3 px-5 text-right font-bold text-emerald-800">7,5</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-5 font-medium">Øvrigt byggeri, fx skoler og institutioner</td>
                      <td className="py-3 px-5 text-right font-bold text-emerald-800">8,0</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-sm text-stone-600">
                Resultatet afrundes til én decimal, før det sammenlignes med grænsen. Har bygningen blandet anvendelse, beregnes en arealvægtet grænseværdi.
              </p>
            </div>

            {/* AFSNIT 3 */}
            <div className="space-y-4 pt-4">
              <h2 className="text-2xl font-bold text-stone-900 tracking-tight">
                Byggepladsen tæller nu med
              </h2>
              <p>
                Den største praktiske nyhed er, at byggeprocessen – i LCA-sprog modulerne A4 og A5 – skal opgøres og overholde en selvstændig grænse på <strong>1,5 kg CO2e pr. m² pr. år</strong>. Det dækker bl.a.:
              </p>
              <ul className="space-y-2 pl-4 list-disc marker:text-emerald-700 text-stone-700 text-sm sm:text-base">
                <li>Transport af materialer fra fabrik til byggeplads</li>
                <li>Transport af materiel til og fra pladsen</li>
                <li>El, varme og brændstof brugt på byggepladsen</li>
                <li>Bortkørsel og behandling af byggeaffald og jord</li>
              </ul>
              <p>
                Det betyder, at entreprenøren skal registrere forbrug og gemme dokumentation undervejs – fx elaflæsninger, brændstofforbrug og affaldsfakturaer. Maskiner under 1 ton kan udelades. For transport af materialer findes der standardværdier i bygningsreglementet, som gør beregningen enklere. Det vigtigste er at aftale fra start, hvem der samler data – ellers ender man med at lede efter bilag, når huset skal færdigmeldes.
              </p>
            </div>

            {/* AFSNIT 4 */}
            <div className="space-y-4 pt-4">
              <h2 className="text-2xl font-bold text-stone-900 tracking-tight">
                Hvem er undtaget?
              </h2>
              <p>
                Ikke alt byggeri skal have en LCA. Undtaget er bl.a.:
              </p>
              <ul className="space-y-2 pl-4 list-disc marker:text-emerald-700 text-stone-700 text-sm sm:text-base">
                <li>Uopvarmede bygninger under 50 m², fx udhuse og carporte</li>
                <li>Tilbygninger under 250 m² opvarmet etageareal til enfamiliehuse, stuehuse, række-, kæde- og dobbelthuse og sommerhuse</li>
                <li>Landbrugets avls- og driftsbygninger</li>
                <li>Transportable konstruktioner og midlertidige pavilloner</li>
              </ul>
              <p>
                Visse samfundskritiske bygninger, fx hospitaler, vandværker og bygninger til energiproduktion, skal have en beregning, men er undtaget fra grænseværdien. Renovering og ombygning er slet ikke omfattet af klimakravene.
              </p>
            </div>

            {/* AFSNIT 5 */}
            <div className="space-y-4 pt-4">
              <h2 className="text-2xl font-bold text-stone-900 tracking-tight">
                Hvornår skal LCA'en laves?
              </h2>
              <p>
                Formelt kræver bygningsreglementet én klimaberegning, som indsendes med færdigmeldingen. Uden den kan kommunen ikke give ibrugtagningstilladelse. Bygningsejeren har ansvaret.
              </p>
              <p>
                I praksis er det en dårlig idé at vente til slutningen. Ligger huset over grænsen, når det står færdigt, er det dyrt – eller umuligt – at rette. Derfor bør der laves en foreløbig beregning i projekteringen, mens der stadig kan skrues på fundament, konstruktion, isolering og facade. Den endelige beregning opdateres så med de faktiske materialer og byggepladsdata.
              </p>
            </div>

            {/* AFSNIT 6 */}
            <div className="space-y-4 pt-4">
              <h2 className="text-2xl font-bold text-stone-900 tracking-tight">
                Hvad påvirker resultatet mest?
              </h2>
              <p>
                Typisk er det de tunge bygningsdele, der vejer mest: fundament og terrændæk, ydervægge, tag og vinduer. Også energiforbruget i driften indgår, og derfor hænger LCA'en tæt sammen med{" "}
                <Link to="/energiramme" className="text-emerald-700 font-semibold hover:underline">
                  energirammen
                </Link>
                . Et hus med en effektiv varmepumpe og en god klimaskærm står stærkere. Produkter med en miljøvaredeklaration (EPD) kan give et mere præcist – og ofte bedre – resultat end de generiske standardværdier.
              </p>
            </div>

            {/* TJEKLISTE */}
            <div className="my-8 p-6 sm:p-8 rounded-3xl bg-stone-50 border border-stone-200 space-y-4">
              <h3 className="text-xl font-bold text-stone-900 tracking-tight flex items-center gap-2">
                <i className="fa-solid fa-list-check text-emerald-700" aria-hidden="true"></i>
                <span>Tjekliste: Sådan kommer du godt i mål</span>
              </h3>
              <p className="text-xs sm:text-sm text-stone-600">
                Uanset om du er privat bygherre eller entreprenør, kan disse fem trin spare både tid og ærgrelser:
              </p>

              <ol className="space-y-3 pl-4 list-decimal marker:font-bold marker:text-emerald-800 text-stone-700 text-sm sm:text-base">
                <li>Afklar ved byggeansøgningen, hvilken grænseværdi der gælder for netop dit byggeri.</li>
                <li>Aftal skriftligt, hvem der laver LCA'en, og hvem der samler byggepladsdata – bygherre, entreprenør eller typehusfirma.</li>
                <li>Få lavet en foreløbig beregning på tegningerne, før materialerne bestilles.</li>
                <li>Giv byggelederen en registreringsliste til el, brændstof, transporter og affald fra første dag på pladsen.</li>
                <li>Opdater beregningen med de faktiske materialer, og indsend den med færdigmeldingen.</li>
              </ol>

              <p className="text-xs sm:text-sm text-stone-600 pt-2 border-t border-stone-200">
                Følger du de fem trin, er der sjældent overraskelser, når huset skal tages i brug. Og bliver resultatet alligevel stramt, er der stadig tid til at vælge et andet produkt eller en anden løsning.
              </p>
            </div>

            {/* AFSNIT 7 */}
            <div className="space-y-4 pt-4">
              <h2 className="text-2xl font-bold text-stone-900 tracking-tight">
                Hvad med 2027?
              </h2>
              <p>
                Den politiske aftale bag kravene lægger op til, at grænseværdierne strammes yderligere i 2027 og 2029. For enfamiliehuse er pejlemærket 6,0 i 2027 og 5,4 i 2029, og for byggeprocessen 1,3 og 1,1. Værdierne er dog ikke endeligt besluttet endnu. Tjek altid, hvad der gælder på det tidspunkt, hvor byggetilladelsen søges.
              </p>
            </div>

            {/* SÅDAN KAN VI HJÆLPE */}
            <div className="my-8 p-8 rounded-3xl bg-gradient-to-br from-[#11463A] to-[#165B4C] text-white shadow-xl space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-400/20 text-emerald-200 border border-emerald-400/30 text-xs font-semibold uppercase tracking-wider">
                <i className="fa-solid fa-handshake text-xs" aria-hidden="true"></i>
                <span>Samlet BR18-løsning</span>
              </div>
              <h3 className="text-2xl font-bold text-white tracking-tight">
                Sådan kan vi hjælpe
              </h3>
              <p className="text-stone-200 text-sm sm:text-base leading-relaxed">
                Hos ET Danmark laver vi LCA-beregningen sammen med energirammen, trykprøven og energimærket. Med mere end 20 års erfaring i byggebranchen kender vi både tegningerne og byggepladsen. Du får én kontaktperson, én fast pris og al BR18-dokumentationen samlet – i hele Jylland og på Fyn. LCA-beregningen leveres inden for 5 arbejdsdage efter modtagelse af komplet materiale (tegninger, mængder eller BIM-model), og bestiller du den sammen med energiramme og energimærke, får du 10 % rabat på den samlede pris – også hvis trykprøve lægges med i samme forløb.
              </p>
              <div className="pt-2">
                <Link
                  to="/lca-beregning"
                  className="px-6 py-3 rounded-full bg-white hover:bg-emerald-50 text-emerald-950 font-extrabold text-sm inline-flex items-center gap-2 transition-colors shadow-md"
                >
                  <span>Få et uforpligtende tilbud på din LCA</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
}
